import { mapSetEsriAttribution } from '@features/map/model/actions.js';
import toastsClasses from '@features/toasts/components/Toasts.module.css';
import { toastsAdd, toastsRemove } from '@features/toasts/model/actions.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import z from 'zod';

const EsriWorldImageryAttributionSchema = z.object({
  contributors: z.array(
    z.object({
      attribution: z.string(),
      coverageAreas: z.array(
        z.object({
          zoomMax: z.number().optional(),
          zoomMin: z.number().optional(),
          score: z.number(),
          bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
        }),
      ),
    }),
  ),
});

type EsriWorldImageryAttribution = z.infer<
  typeof EsriWorldImageryAttributionSchema
>;

function isIntersecting(
  caBbox: number[],
  mapBounds: [number, number, number, number],
): boolean {
  return !(
    caBbox[3] < mapBounds[0] ||
    caBbox[2] < mapBounds[1] ||
    caBbox[1] > mapBounds[2] ||
    caBbox[0] > mapBounds[3]
  );
}

export function useAttributionInfo() {
  const [nonce, setNonce] = useState(0);

  const dispatch = useDispatch();

  const layers = useAppSelector((state) => state.map.layers);

  const licenceShownForRef = useRef([
    new Set<string>(),
    new Set<string>(),
  ] as const);

  const prevNonceRef = useRef(0);

  const showingAttribution = useAppSelector(
    (state) => 'attribution' in state.toasts.toasts,
  );

  // hide attribution on mouse down
  useEffect(() => {
    if (!showingAttribution) {
      return;
    }

    const handlePointerDown = (e: MouseEvent) => {
      if (e.target instanceof Element) {
        let el: Element | null = e.target;

        while (el) {
          if (
            el instanceof HTMLElement &&
            el.classList.contains(toastsClasses.toasts)
          ) {
            return;
          }

          el = el.parentElement;
        }
      }

      dispatch(toastsRemove('attribution'));
    };

    document.body.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.body.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [dispatch, showingAttribution]);

  const [esriAttributions, setEsriAttributions] = useState<
    EsriWorldImageryAttribution | undefined
  >(undefined);

  const esriAttribution = useAppSelector((state) => state.map.esriAttribution);

  const bounds = useAppSelector((state) => state.map.bounds);

  const zoom = useAppSelector((state) =>
    state.map.zoom + (window.devicePixelRatio || 1) > 1.4 ? 1 : 0,
  );

  useEffect(() => {
    if (!layers.includes('S') || !esriAttributions || !bounds) {
      if (esriAttribution.length > 0) {
        dispatch(mapSetEsriAttribution([]));
      }

      return;
    }

    const a = esriAttributions.contributors.filter((c) =>
      c.coverageAreas.some(
        (ca) =>
          (ca.zoomMin === undefined || ca.zoomMin <= zoom) &&
          (ca.zoomMax === undefined || ca.zoomMax >= zoom) &&
          isIntersecting(ca.bbox, bounds),
      ),
    );

    const attributions = a.map((a) => a.attribution);

    if (attributions.join('\n') !== esriAttribution.join('\n')) {
      dispatch(mapSetEsriAttribution(attributions));
    }
  }, [esriAttributions, esriAttribution, zoom, bounds, dispatch, layers]);

  const ea = useRef(false);

  useEffect(() => {
    async function fetchAttributions() {
      if (ea.current || !layers.includes('S')) {
        return;
      }

      ea.current = true;

      const res = await fetch(
        'https://static.arcgis.com/attribution/World_Imagery',
      );

      if (res.ok) {
        setEsriAttributions(
          EsriWorldImageryAttributionSchema.parse(await res.json()),
        );
      }
    }

    fetchAttributions(); // TODO handle error
  }, [layers]);

  useEffect(() => {
    if (window.isRobot) {
      return;
    }

    const [mapLayers, esriAttributions] = licenceShownForRef.current;

    if (
      layers.every((o) => mapLayers.has(o)) &&
      esriAttribution.every((a) => esriAttributions.has(a)) &&
      prevNonceRef.current === nonce
    ) {
      return;
    }

    prevNonceRef.current = nonce;

    for (const o of layers) {
      mapLayers.add(o);
    }

    for (const a of esriAttribution) {
      esriAttributions.add(a);
    }

    dispatch(
      toastsAdd({
        id: 'attribution',
        messageKey: 'general.attribution',
        style: 'info',
        timeout: 5000,
      }),
    );
  }, [layers, dispatch, nonce, esriAttribution]);

  return useCallback(() => {
    setNonce((n) => n + 1);
  }, []);
}
